import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";

export default function Contact() {
  useSEO({
    title: "Contact SE Distribution Malaysia | Sales Enquiry, Technical Support | Kuala Lumpur",
    description: "Contact SE Distribution Sdn Bhd in Kuala Lumpur — sales enquiries, technical support, and solution consultations for Hytera, Arcfra, ExaGrid, rugged computing & surveillance. Call +603 6412 7917.",
    canonical: "https://www.sedistribution.com.my/contact",
    keywords: "contact SE Distribution, SE Distribution phone, SE Distribution email, enterprise IT Malaysia contact, Hytera Malaysia contact, Arcfra Malaysia contact",
  });
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    inquiry_type: "General Inquiry",
    message: "",
  });
  const [charCount, setCharCount] = useState(0);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "message") setCharCount(value.length);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    const data = new URLSearchParams();
    Object.entries(formData).forEach(([k, v]) => data.append(k, v));
    try {
      await fetch("https://readdy.ai/api/form/d7j1qb83a2kq2q6kqn3g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
    } catch {
      // silent
    }
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 pb-20">
        {/* Hero */}
        <div className="bg-[#f9f7f4] py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">
              Get In Touch
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-[#1a1a1a] mb-4">
              Contact Us for a Custom Solution
            </h1>
            <p className="text-gray-500 text-sm max-w-lg mx-auto leading-relaxed">
              Tell us about your business challenges and our solutions architects will design a tailored technology package just for you — at no obligation.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Info */}
            <div className="lg:col-span-1">
              <h2 className="text-lg font-bold text-[#1a1a1a] mb-6">Our Office</h2>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/5 rounded-md flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-[#1a1a1a] text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1a1a1a]">Address</p>
                    <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">
                      Unit 21-11, Menara 1MK Kompleks,<br />
                      Mont Kiara, 50480 Kuala Lumpur,<br />
                      Malaysia
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/5 rounded-md flex-shrink-0 mt-0.5">
                    <i className="ri-phone-line text-[#1a1a1a] text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1a1a1a]">Phone</p>
                    <a href="tel:+60364127917" className="text-sm text-gray-500 mt-0.5 hover:text-[#1a1a1a] transition-colors">
                      +603 6412 7917
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/5 rounded-md flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line text-[#1a1a1a] text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1a1a1a]">Email</p>
                    <a href="mailto:sales@sedistribution.com.my" className="text-sm text-gray-500 mt-0.5 hover:text-[#1a1a1a] transition-colors">
                      sales@sedistribution.com.my
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/5 rounded-md flex-shrink-0 mt-0.5">
                    <i className="ri-time-line text-[#1a1a1a] text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1a1a1a]">Business Hours</p>
                    <p className="text-sm text-gray-500 mt-0.5">Monday – Friday</p>
                    <p className="text-sm text-gray-500">9:30 AM – 6:30 PM (GMT+8)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/5 rounded-md flex-shrink-0 mt-0.5">
                    <i className="ri-building-line text-[#1a1a1a] text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1a1a1a]">Registration</p>
                    <p className="text-sm text-gray-500 mt-0.5">SE Distribution Sdn Bhd</p>
                    <p className="text-sm text-gray-500">202001036939 (1393260-U)</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-8 rounded-lg overflow-hidden border border-gray-100 w-full h-52">
                <iframe
                  title="SE Distribution Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.6!2d101.6503!3d3.1726!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc49c701efeae7%3A0x1f3e3e3e3e3e3e3e!2sMenara%201MK%2C%20Mont%20Kiara%2C%2050480%20Kuala%20Lumpur!5e0!3m2!1sen!2smy!4v1700000000000!5m2!1sen!2smy"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="bg-[#f9f7f4] rounded-lg p-10 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#1a1a1a] rounded-full mx-auto mb-5">
                    <i className="ri-check-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a1a1a] mb-2">Message Received!</h3>
                  <p className="text-gray-500 text-sm">
                    Thank you for reaching out. Our solutions team will be in touch within 1 business day.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="contact-inquiry-form"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                >
                  <div>
                    <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Ahmad bin Abdullah"
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2.5 outline-none focus:border-[#1a1a1a] bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="ahmad@company.com.my"
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2.5 outline-none focus:border-[#1a1a1a] bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+60 12-xxx xxxx"
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2.5 outline-none focus:border-[#1a1a1a] bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company Sdn Bhd"
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2.5 outline-none focus:border-[#1a1a1a] bg-white"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Inquiry Type</label>
                    <select
                      name="inquiry_type"
                      value={formData.inquiry_type}
                      onChange={handleChange}
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2.5 outline-none focus:border-[#1a1a1a] bg-white cursor-pointer"
                    >
                      <option>General Inquiry</option>
                      <option>Surveillance &amp; Security Solutions</option>
                      <option>Rugged Computing Solutions</option>
                      <option>Data Protection &amp; Backup Solutions</option>
                      <option>Body-Worn Video (BWV) Devices</option>
                      <option>Technical Support</option>
                      <option>Partnership Inquiry</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">
                      Your Message *
                      <span className={`ml-2 font-normal ${charCount > 500 ? "text-red-500" : "text-gray-400"}`}>
                        ({charCount}/500)
                      </span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      maxLength={500}
                      placeholder="Describe your business challenge or the solution you are looking for..."
                      className="w-full text-sm border border-gray-200 rounded-md px-3 py-2.5 outline-none focus:border-[#1a1a1a] bg-white resize-none"
                    />
                    {charCount > 500 && (
                      <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={charCount > 500}
                      className="bg-[#1a1a1a] text-white text-sm font-semibold px-8 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      Send Message
                      <i className="ri-send-plane-line"></i>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
